// ─────────────────────────────────────────────────────────────────────────────
//  BillingDischargeLockSettings — "Discharge before billing"
//
//  Whether accounts must wait for a nurse to complete the discharge before the
//  patient's bill can be settled.
//
//  THE RULE THIS SWITCHES
//
//  A doctor's discharge hands the ward a work item. While it stands, the billing
//  screen is locked: accounts cannot take payment until a nurse completes the
//  discharge. It exists so a patient cannot be billed and walk out mid-discharge
//  with the bed still held and the last day's charges unrecorded.
//
//  It is a policy, not a law. In a small facility the same person often does
//  both jobs and the lock only gets in the way, so it can be turned off.
//
//  Worded as ON / OFF rather than as three modes, because there are genuinely
//  only two states here — unlike the payment gates, which have a middle setting
//  ("see it but do not act on it") that this rule has no equivalent of.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Stack, Radio, RadioGroup, FormControlLabel, FormControl, Typography,
  Button, CircularProgress, Alert, Chip,
} from '@mui/material';
import {
  SaveOutlined as SaveIcon,
  LockOutlined as LockIcon,
  LockOpenOutlined as UnlockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import { billingDischargeLock } from '../BaseURL';

const MODES = [
  {
    value: 'on',
    icon: <LockIcon fontSize="small" />,
    title: 'Wait for the nurse to complete the discharge',
    sees: 'Accounts sees the bill locked while the ward still has the patient.',
    may:  'Payment can only be taken once a nurse has completed the discharge and freed the bed.',
    isDefault: true,
  },
  {
    value: 'off',
    icon: <UnlockIcon fontSize="small" />,
    title: 'Bill as soon as the doctor discharges',
    sees: 'Accounts sees the bill straight away.',
    may:  'Payment can be taken without waiting for the ward. The nurse still has to complete the discharge — the bed is still held until they do — but accounts is no longer made to wait for it.',
  },
];

const BillingDischargeLockSettings = () => {
  const [mode, setMode]       = useState('on');
  const [saved, setSaved]     = useState('on');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(billingDischargeLock);
      const v = res.data?.enabled;
      const next = v === 'off' ? 'off' : 'on';
      setMode(next);
      setSaved(next);
    } catch (err) {
      /*  Shows ON when the setting cannot be read, which matches how the server
       *  behaves: it leaves the rule enforced on any read failure. Showing OFF
       *  here would tell an administrator the lock was lifted when it is not. */
      setMode('on');
      setSaved('on');
      setError(
        err.response?.status === 404
          ? 'This server does not have the setting yet — deploy the latest backend. '
            + 'Until then the rule stays on.'
          : (err.response?.data?.message || err.message || 'Could not load the setting.')
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (mode === saved) return;

    //  Only the OFF direction is confirmed. Turning the rule back ON restores
    //  the stricter behaviour and needs no warning.
    if (mode === 'off') {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Allow billing before the nurse discharges?',
        html: `
          <div style="text-align:left;font-size:0.9rem">
            <p>Accounts will be able to settle a bill as soon as the doctor discharges,
               without waiting for the ward.</p>
            <p style="color:#b45309">A patient can then pay and leave while the discharge
               is still open — the bed stays held and anything charged afterwards will
               miss the bill.</p>
            <p>The nurse's discharge is still required and still appears on their worklist.</p>
          </div>`,
        showCancelButton: true,
        confirmButtonColor: '#b45309',
        cancelButtonColor: '#9e9e9e',
        confirmButtonText: 'Yes, turn it off',
      });
      if (!confirm.isConfirmed) return;
    }

    setSaving(true);
    try {
      const res = await axios.put(billingDischargeLock, { enabled: mode });
      if (res.data?.success) {
        setSaved(res.data.enabled);
        setMode(res.data.enabled);
        await Swal.fire({
          icon: 'success', title: 'Saved',
          text: 'The billing screen will pick this up within 30 seconds.',
          timer: 2200, showConfirmButton: false,
        });
      } else {
        throw new Error(res.data?.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        icon: 'error', title: 'Save Failed',
        text: err.response?.data?.message || err.message,
        confirmButtonColor: '#c0392b',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="warning" sx={{ fontSize: '0.82rem' }}>{error}</Alert>}

      <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>
        This affects the <strong>billing screen only</strong>. Whichever way it is set,
        a doctor's discharge still creates the ward's work item and the bed is still
        held until a nurse completes it.
      </Typography>

      <FormControl>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODES.map(m => (
            <Box
              key={m.value}
              sx={{
                border: '1px solid',
                borderColor: mode === m.value ? '#11998e' : '#e2e8f0',
                bgcolor: mode === m.value ? 'rgba(17,153,142,0.04)' : '#fff',
                borderRadius: 2, p: 1.5, mb: 1, transition: 'all .15s',
              }}
            >
              <FormControlLabel
                value={m.value}
                control={<Radio size="small" sx={{ color: '#11998e', '&.Mui-checked': { color: '#11998e' } }} />}
                sx={{ alignItems: 'flex-start', m: 0 }}
                label={
                  <Box sx={{ pt: 0.25 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                      {m.icon}
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
                        {m.title}
                      </Typography>
                      {m.isDefault && (
                        <Chip label="Default" size="small"
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                bgcolor: 'rgba(100,116,139,0.12)', color: '#475569' }} />
                      )}
                      {saved === m.value && (
                        <Chip label="Active" size="small"
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                bgcolor: 'rgba(16,185,129,0.14)', color: '#15803D' }} />
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: '0.76rem', color: '#475569' }}>
                      <strong>Accounts sees:</strong> {m.sees}
                    </Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: '#475569' }}>
                      <strong>May do:</strong> {m.may}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          ))}
        </RadioGroup>
      </FormControl>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          variant="contained" onClick={handleSave}
          disabled={saving || mode === saved}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
          sx={{ textTransform: 'none', fontWeight: 700, background: '#11998e',
                '&:hover': { background: '#0e8a7f' } }}
        >
          {saving ? 'Saving…' : mode === saved ? 'Saved' : 'Save change'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default BillingDischargeLockSettings;
